import { type HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  type EnvironmentProviders,
  type Provider,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initToken } from './services/token.service';
import { BrandingService, initBranding } from './services/branding.service';
import { initKundeToken } from './services/kunde-token.service';
import { sessionInterceptor } from './interceptors/session.interceptor';
import { kundeTokenInterceptor } from './interceptors/kunde-token.interceptor';
import { SPARTE_API_BASE_PATH } from './sparte-api-base-path.token';

export interface InfrastructureOptions {
  /**
   * The app's `environment.apiUrl`. A library cannot read an environment file — there is no path
   * to one and `enforce-module-boundaries` forbids reaching into an app — so the app hands it in.
   * A branded deployment overrides it through `BrandConfig.apiUrl`.
   */
  apiUrl: string;

  /**
   * App-owned interceptors. They are placed *outside* `kundeTokenInterceptor`,
   * so a retry one of them issues passes through the token attach again and
   * picks up a freshly issued token. This is how an app adds policy — reacting
   * to a 401 by asking the customer to log in, say — without this library
   * having to know that policy exists.
   */
  interceptors?: HttpInterceptorFn[];
}

export function infrastructureProviders(
  options: InfrastructureOptions
): (Provider | EnvironmentProviders)[] {
  return [
    // The one place the API root is decided. Apps alias their generated BASE_PATH onto it, so the
    // generated clients and the libraries' hand-written calls can never drift apart.
    {
      provide: SPARTE_API_BASE_PATH,
      useFactory: () => inject(BrandingService).config()?.apiUrl ?? options.apiUrl,
    },
    // Load-bearing since Angular 21: bootstrapApplication now prepends its own zoneless
    // providers, so an app that provides neither runs with NoopNgZone. This call comes
    // later in the array and overrides NgZone and ZONELESS_ENABLED back to zoned. Angular's
    // bootstrap-options-migration adds a bare provideZoneChangeDetection() to every
    // main.ts because it cannot see this one — that addition is redundant and was reverted;
    // drop it again on the next major. We stay zoned deliberately: input-errors.component
    // relies on a global CD pass (see the OnPush note there).
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(
      withInterceptors([sessionInterceptor, ...(options.interceptors ?? []), kundeTokenInterceptor])
    ),
    // Branding must bootstrap before the token initializer: initToken needs the
    // active brand to apply the MLP session fallback when no extVermittlerId is present.
    provideAppInitializer(initBranding()),
    provideAppInitializer(initToken()),
    provideAppInitializer(initKundeToken()),
  ];
}
